import {useQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {useAnalytics} from '@github-ui/use-analytics'
import {useDebounce} from '@github-ui/use-debounce'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo, useState} from 'react'

import type {OcticonName} from '../../types'

interface Repository {
  id: number
  name: string
  owner: string
  nameWithOwner: string
  iconType: OcticonName
}

const RepositoriesResponseSchema = Type.Object({
  repositories: Type.Array(
    Type.Object({
      id: Type.Number(),
      name: Type.String(),
      owner: Type.String(),
      nameWithOwner: Type.String(),
      iconType: Type.String(),
    }),
  ),
})

const RepositoriesResponseValidator = Compile(RepositoriesResponseSchema)

interface RepositoriesResponse {
  repositories: Repository[]
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000

export interface UseRepositorySuggestionsOptions {
  owner: string
  enabled: boolean
  currentRepositoryName: string
  currentRepositoryIconType: OcticonName
}

export interface UseRepositorySuggestionsResult {
  repositories: Repository[]
  isLoading: boolean
  searchQuery: string
  handleFilterChange: (value: string) => void
}

export function useRepositorySuggestions({
  owner,
  enabled,
  currentRepositoryName,
  currentRepositoryIconType,
}: UseRepositorySuggestionsOptions): UseRepositorySuggestionsResult {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createPersister({
        validator: RepositoriesResponseValidator,
        sendAnalyticsEvent,
        prefix: 'global-nav-repo-picker',
        maxAgeMs: ONE_DAY_MS,
      }),
    [sendAnalyticsEvent],
  )

  // Debounce the setter to avoid sending requests on every keystroke
  const debouncedSetQuery = useDebounce((value: string) => setDebouncedSearchQuery(value), 300, {
    leading: false,
    trailing: true,
  })

  const handleFilterChange = useCallback(
    (value: string) => {
      setSearchQuery(value)
      debouncedSetQuery(value)
    },
    [debouncedSetQuery],
  )

  const {data, isFetching} = useQuery<RepositoriesResponse>({
    queryKey: ['global-nav-repo-picker', owner, debouncedSearchQuery],
    queryFn: async ({signal}) => {
      if (!owner) {
        return {repositories: []}
      }

      const url = new URL(`/_global-navigation/repositories/${encodeURIComponent(owner)}`, window.location.origin)
      if (debouncedSearchQuery) {
        url.searchParams.set('query', debouncedSearchQuery)
      }

      // Pass signal to allow request cancellation
      const response = await reactFetchJSON(url.toString(), {signal})

      if (!response.ok) {
        return {repositories: []}
      }

      return response.json() as Promise<RepositoriesResponse>
    },
    persister,
    placeholderData: previousData => previousData,
    enabled: !!owner && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  let currentRepositoryObject: Repository | null = null
  const repositories = [] as Repository[]
  for (const repo of data?.repositories ?? []) {
    if (repo.name === currentRepositoryName) {
      currentRepositoryObject = repo
    } else {
      repositories.push(repo)
    }
  }

  if (currentRepositoryObject) {
    // Ensure the current repository appears at the top of the list
    repositories.unshift(currentRepositoryObject)
  } else if (currentRepositoryName.toLowerCase().includes(searchQuery.toLowerCase())) {
    // If the current repository is not in the results but matches the search query, add placeholder to the top
    // Use the immediate searchQuery (not debounced) for instant UI feedback
    repositories.unshift({
      id: -1,
      name: currentRepositoryName,
      owner,
      nameWithOwner: `${owner}/${currentRepositoryName}`,
      iconType: currentRepositoryIconType,
    })
  }

  return {
    repositories,
    isLoading: isFetching || searchQuery !== debouncedSearchQuery,
    searchQuery,
    handleFilterChange,
  }
}

export type {Repository}
